import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { maaserAPI, incomeAPI, expenseAPI } from '../services/api';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiPercent,
  FiCheckCircle,
  FiAlertCircle,
  FiCreditCard,
  FiPlus,
} from 'react-icons/fi';

const HEBREW_MONTHS = [
  'ינואר','פברואר','מרץ','אפריל','מאי','יוני',
  'יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר',
];

const INCOME_CATEGORIES = ['משכורת','בונוס','פרילנס','השקעות','שכר דירה','מתנות','החזרים','אחר'];
const EXPENSE_CATEGORIES = ['שכר דירה / משכנתא','חשבונות','אוכל','תחבורה','בריאות','חינוך','בגדים','בילויים','צדקה','חיסכון','אחר'];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [activeTab, setActiveTab] = useState('income');
  const [quickForm, setQuickForm] = useState({
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    recipient: '',
  });
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickSuccess, setQuickSuccess] = useState('');
  const [quickError, setQuickError] = useState('');

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const response = await maaserAPI.getSummary({
        month: selectedMonth,
        year: selectedYear,
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error loading summary:', error);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const formatCurrency = (value) => {
    if (value == null) return '₪0';
    return `₪${Number(value).toLocaleString('he-IL')}`;
  };

  const getValueClass = (value) => {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return '';
  };

  const handleQuickChange = (e) => {
    const { name, value } = e.target;
    setQuickForm(prev => ({ ...prev, [name]: value }));
  };

  const resetQuickForm = () => {
    setQuickForm({
      amount: '',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      recipient: '',
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setQuickError('');
    setQuickSuccess('');
    resetQuickForm();
  };

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    setQuickError('');
    setQuickSuccess('');
    setQuickLoading(true);

    try {
      if (activeTab === 'income') {
        await incomeAPI.create({
          amount: Number(quickForm.amount),
          category: quickForm.category,
          description: quickForm.description,
          date: quickForm.date,
        });
        setQuickSuccess('ההכנסה נוספה בהצלחה!');
      } else if (activeTab === 'expense') {
        await expenseAPI.create({
          amount: Number(quickForm.amount),
          category: quickForm.category,
          description: quickForm.description,
          date: quickForm.date,
        });
        setQuickSuccess('ההוצאה נוספה בהצלחה!');
      } else {
        await maaserAPI.addPayment({
          amount: Number(quickForm.amount),
          recipient: quickForm.recipient,
          description: quickForm.description,
          date: quickForm.date,
        });
        setQuickSuccess('תשלום המעשר נרשם בהצלחה!');
      }
      resetQuickForm();
      fetchSummary();
      setTimeout(() => setQuickSuccess(''), 3000);
    } catch (err) {
      setQuickError(err.response?.data?.error || 'שגיאה בשמירה. נסה שנית.');
    } finally {
      setQuickLoading(false);
    }
  };

  const currentCategories = activeTab === 'income' ? INCOME_CATEGORIES : activeTab === 'expense' ? EXPENSE_CATEGORIES : [];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  const filterBar = (
    <div className="filter-bar">
      <select className="form-control" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
        {HEBREW_MONTHS.map((month, index) => (
          <option key={index + 1} value={index + 1}>{month}</option>
        ))}
      </select>
      <select className="form-control" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
        {Array.from({ length: 11 }, (_, i) => 2020 + i).map((year) => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>
    </div>
  );

  if (!summary) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>דשבורד</h1>
          <p className="page-subtitle">סיכום הכנסות, הוצאות ומעשרות</p>
        </div>
        {filterBar}
        <div className="empty-state">
          <p>אין נתונים להצגה עבור התקופה שנבחרה</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>{user?.first_name ? `שלום, ${user.first_name}` : 'דשבורד'}</h1>
        <p className="page-subtitle">
          סיכום חודשי - {HEBREW_MONTHS[selectedMonth - 1]} {selectedYear}
        </p>
      </div>

      {filterBar}

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card" onClick={() => navigate('/incomes')} style={{cursor: 'pointer'}}>
          <div className="summary-card-icon" style={{ background: '#dcfce7', color: '#059669' }}>
            <FiTrendingUp />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">הכנסות</span>
            <span className={`summary-card-value ${getValueClass(summary.total_income)}`}>
              {formatCurrency(summary.total_income)}
            </span>
          </div>
        </div>

        <div className="summary-card" onClick={() => navigate('/expenses')} style={{cursor: 'pointer'}}>
          <div className="summary-card-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
            <FiTrendingDown />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">הוצאות</span>
            <span className="summary-card-value">
              {formatCurrency(summary.total_expenses)}
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>
            <FiDollarSign />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">רווח נקי</span>
            <span className={`summary-card-value ${getValueClass(summary.net_profit)}`}>
              {formatCurrency(summary.net_profit)}
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
            <FiPercent />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">מעשר ({summary.maaser_percentage}%)</span>
            <span className="summary-card-value">
              {formatCurrency(summary.maaser_required)}
            </span>
          </div>
        </div>

        <div className="summary-card" onClick={() => navigate('/maaser')} style={{cursor: 'pointer'}}>
          <div className="summary-card-icon" style={{ background: '#d1fae5', color: '#059669' }}>
            <FiCheckCircle />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">שולם</span>
            <span className="summary-card-value positive">
              {formatCurrency(summary.maaser_paid)}
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon" style={{ background: '#fce7f3', color: '#db2777' }}>
            <FiAlertCircle />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">נותר</span>
            <span className={`summary-card-value ${summary.maaser_remaining > 0 ? 'negative' : ''}`}>
              {formatCurrency(summary.maaser_remaining)}
            </span>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon" style={{ background: '#e0e7ff', color: '#7c3aed' }}>
            <FiCreditCard />
          </div>
          <div className="summary-card-info">
            <span className="summary-card-label">לאחר מעשר</span>
            <span className={`summary-card-value ${getValueClass(summary.after_maaser)}`}>
              {formatCurrency(summary.after_maaser)}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Add Section */}
      <div className="quick-add-section">
        <div className="quick-add-card">
          <h3 className="quick-add-title">
            <FiPlus /> הוספה מהירה
          </h3>

          <div className="quick-add-tabs">
            <button
              className={`quick-add-tab ${activeTab === 'income' ? 'active income' : ''}`}
              onClick={() => handleTabChange('income')}
            >
              הכנסה
            </button>
            <button
              className={`quick-add-tab ${activeTab === 'expense' ? 'active expense' : ''}`}
              onClick={() => handleTabChange('expense')}
            >
              הוצאה
            </button>
            <button
              className={`quick-add-tab ${activeTab === 'maaser' ? 'active maaser' : ''}`}
              onClick={() => handleTabChange('maaser')}
            >
              תשלום מעשר
            </button>
          </div>

          {quickSuccess && <div className="alert-success">{quickSuccess}</div>}
          {quickError && <div className="alert-error">{quickError}</div>}

          <form onSubmit={handleQuickSubmit} className="quick-add-form">
            <div className="quick-add-row">
              <div className="form-group">
                <label>סכום (₪)</label>
                <input
                  type="number"
                  name="amount"
                  className="form-control"
                  value={quickForm.amount}
                  onChange={handleQuickChange}
                  placeholder="0.00"
                  required
                  min="0.01"
                  step="0.01"
                  disabled={quickLoading}
                />
              </div>

              {activeTab !== 'maaser' ? (
                <div className="form-group">
                  <label>קטגוריה</label>
                  <select
                    name="category"
                    className="form-control"
                    value={quickForm.category}
                    onChange={handleQuickChange}
                    required
                    disabled={quickLoading}
                  >
                    <option value="">בחר קטגוריה</option>
                    {currentCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>נמען / ארגון</label>
                  <input
                    type="text"
                    name="recipient"
                    className="form-control"
                    value={quickForm.recipient}
                    onChange={handleQuickChange}
                    placeholder="שם הנמען"
                    disabled={quickLoading}
                  />
                </div>
              )}

              <div className="form-group">
                <label>תיאור</label>
                <input
                  type="text"
                  name="description"
                  className="form-control"
                  value={quickForm.description}
                  onChange={handleQuickChange}
                  placeholder="תיאור קצר"
                  disabled={quickLoading}
                />
              </div>

              <div className="form-group">
                <label>תאריך</label>
                <input
                  type="date"
                  name="date"
                  className="form-control"
                  value={quickForm.date}
                  onChange={handleQuickChange}
                  required
                  disabled={quickLoading}
                />
              </div>
            </div>

            <button
              type="submit"
              className={`btn btn-quick-add ${activeTab === 'income' ? 'btn-success' : activeTab === 'expense' ? 'btn-danger' : 'btn-primary'}`}
              disabled={quickLoading}
            >
              <FiPlus />
              {quickLoading ? 'שומר...' : activeTab === 'income' ? 'הוסף הכנסה' : activeTab === 'expense' ? 'הוסף הוצאה' : 'הוסף תשלום מעשר'}
            </button>
          </form>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="financial-overview">
        <div className="financial-overview-card">
          <h3 className="financial-overview-title">סיכום פיננסי</h3>
          <div className="financial-bars">
            <div className="financial-bar-item" onClick={() => navigate('/incomes')} style={{cursor: 'pointer'}}>
              <div className="financial-bar-header">
                <div className="financial-bar-label">
                  <span className="financial-bar-dot income"></span>
                  <span>הכנסות</span>
                </div>
                <span className="financial-bar-amount income">{formatCurrency(summary.total_income)}</span>
              </div>
              <div className="financial-bar-track">
                <div className="financial-bar-fill income" style={{ width: '100%' }}></div>
              </div>
            </div>

            <div className="financial-bar-item" onClick={() => navigate('/expenses')} style={{cursor: 'pointer'}}>
              <div className="financial-bar-header">
                <div className="financial-bar-label">
                  <span className="financial-bar-dot expense"></span>
                  <span>הוצאות</span>
                </div>
                <span className="financial-bar-amount expense">{formatCurrency(summary.total_expenses)}</span>
              </div>
              <div className="financial-bar-track">
                <div
                  className="financial-bar-fill expense"
                  style={{ width: summary.total_income > 0 ? `${Math.min((summary.total_expenses / summary.total_income) * 100, 100)}%` : '0%' }}
                ></div>
              </div>
            </div>

            <div className="financial-bar-item">
              <div className="financial-bar-header">
                <div className="financial-bar-label">
                  <span className="financial-bar-dot profit"></span>
                  <span>רווח נקי</span>
                </div>
                <span className={`financial-bar-amount ${summary.net_profit >= 0 ? 'profit' : 'expense'}`}>
                  {formatCurrency(summary.net_profit)}
                </span>
              </div>
              <div className="financial-bar-track">
                <div
                  className={`financial-bar-fill ${summary.net_profit >= 0 ? 'profit' : 'expense'}`}
                  style={{ width: summary.total_income > 0 ? `${Math.min(Math.abs(summary.net_profit) / summary.total_income * 100, 100)}%` : '0%' }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Maaser Breakdown */}
        <div className="financial-overview-card maaser-breakdown">
          <h3 className="financial-overview-title">מעשר - {summary.maaser_percentage}%</h3>

          <div className="maaser-summary-row">
            <div className="maaser-summary-item">
              <span className="maaser-summary-label">נדרש</span>
              <span className="maaser-summary-value required">{formatCurrency(summary.maaser_required)}</span>
            </div>
            <div className="maaser-summary-divider"></div>
            <div className="maaser-summary-item">
              <span className="maaser-summary-label">שולם</span>
              <span className="maaser-summary-value paid">{formatCurrency(summary.maaser_paid)}</span>
            </div>
            <div className="maaser-summary-divider"></div>
            <div className="maaser-summary-item">
              <span className="maaser-summary-label">נותר</span>
              <span className={`maaser-summary-value ${summary.maaser_remaining > 0 ? 'remaining' : 'paid'}`}>
                {formatCurrency(summary.maaser_remaining)}
              </span>
            </div>
          </div>

          <div className="maaser-progress">
            <div className="maaser-progress-track">
              <div
                className="maaser-progress-fill paid"
                style={{
                  width: summary.maaser_required > 0
                    ? `${Math.min((summary.maaser_paid / summary.maaser_required) * 100, 100)}%`
                    : '0%'
                }}
              ></div>
            </div>
            <div className="maaser-progress-labels">
              <span className="maaser-progress-percent">
                {summary.maaser_required > 0
                  ? `${Math.round((summary.maaser_paid / summary.maaser_required) * 100)}%`
                  : '0%'
                } שולם
              </span>
              <span className="maaser-progress-remaining">
                {formatCurrency(summary.after_maaser)} נשאר
              </span>
            </div>
          </div>

          <button className="btn btn-outline maaser-detail-btn" onClick={() => navigate('/maaser')}>
            צפה בפירוט מעשר
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
